import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import FetchCalculations from '../api/FetchCalculations';
import handleInputChange from '../function/HandleInputsChange';
import updateCalculations from '../function/UpdateCalculations';
import InfoButton from './InfoButton';
import { useNavigation } from '../function/navigation';
import { UnitConversion } from '../inputs/UnitConversion';

// Module-level caches persist across mounts and component instances
const altCache = new Map(); // key -> results array
const inflightMap = new Map(); // key -> Promise that resolves to results array

function AlternativeResult({
  inputs,
  setInputs,
  options,
  setOptions,
  result,
  compactLayout = false,
}) {
  const { nav, dispatch } = useNavigation();
  const isSvlFamily = inputs?.sv_family === 'SV-L';

  const [altResults, setAltResults] = useState([]);

  const roundToOneDecimal = (value) =>
    typeof value === 'number' ? Math.round(value * 10) / 10 : value;

  const cacheRef = useRef(altCache);

  const triggerKey = useMemo(() => {
    // Build a stable key based only on inputs that affect calculations
    const baseNbr = result?.nbr_windings ?? inputs?.nbr_windings ?? null;
    const keyObj = {
      mt: inputs?.machine_type ?? null,
      h1: inputs?.h1 ?? null,
      h2: inputs?.h2 ?? null,
      h3: inputs?.h3 ?? null,
      cfg: inputs?.configuration ?? null,
      lrAngle: inputs?.l_r_angle ?? null,
      mac: inputs?.max_angle_center ?? null,
      mai: inputs?.max_angle_50mm_inner ?? null,
      rSac: result?.slope_angle_center ?? null,
      nw: baseNbr,
      rPrice: result?.price ?? null,
    };
    return JSON.stringify(keyObj);
  }, [
    inputs?.machine_type,
    inputs?.h1,
    inputs?.h2,
    inputs?.h3,
    inputs?.configuration,
    inputs?.max_angle_center,
    inputs?.max_angle_50mm_inner,
    result?.slope_angle_center,
    result?.nbr_windings,
    inputs?.nbr_windings,
    result?.price,
  ]);

  // Stable click handler
  const handleAlternativeClick = useCallback(
    (alt, index) => {
      handleInputChange(
        'nbr_windings',
        alt.nbr_windings,
        inputs,
        setInputs,
        setOptions
      );
      dispatch({ type: 'addWindingsSelected', payload: index });
    },
    [inputs, setInputs, setOptions, dispatch]
  );

  const fetchDeps = useMemo(() => {
    const baseList = updateCalculations(inputs, options, result) || [];
    const calculationList = [...baseList];

    if (isSvlFamily) {
      if (!calculationList.includes('h2')) calculationList.push('h2');
      if (!calculationList.includes('h3')) calculationList.push('h3');
    }

    return {
      nbr_windings: result?.nbr_windings,
      calculationList,
      inputs,
    };
  }, [result?.nbr_windings, inputs, options, isSvlFamily]);

  const fetchData = useCallback(
    async (nbr_windingsCandidate) => {
      const nbrWindings = fetchDeps.nbr_windings;
      if (Number.isNaN(nbrWindings) || nbrWindings == null || nbrWindings < 0) {
        return null;
      }
      const modifiedInputs = {
        ...fetchDeps.inputs,
        nbr_windings: nbr_windingsCandidate,
      };
      try {
        const r = await FetchCalculations(
          modifiedInputs,
          fetchDeps.calculationList
        );
        return r
          ? {
              nbr_windings: r.nbr_windings,
              h2: r.h2,
              h3: r.h3,
              slope_angle_center: r.slope_angle_center,
              price: r.price,
            }
          : null;
      } catch (error) {
        console.error('Error fetching data:', error);
        return null;
      }
    },
    [fetchDeps]
  );

  // Gate conditions (optional): prevent useless work
  const canFetch = useMemo(() => {
    const nbrWindings = result?.nbr_windings ?? inputs?.nbr_windings;
    if (Number.isNaN(nbrWindings) || nbrWindings == null || nbrWindings < 0)
      return false;

    const anglesTooLow =
      (inputs?.max_angle_50mm_inner ?? Infinity) < 3 &&
      (inputs?.max_angle_center ?? Infinity) < 3;
    if (anglesTooLow) return false;

    return true;
  }, [
    result?.nbr_windings,
    inputs?.nbr_windings,
    inputs?.max_angle_50mm_inner,
    inputs?.max_angle_center,
  ]);

  const loadResults = useCallback(async () => {
    // Reuse from module cache if available
    if (cacheRef.current.has(triggerKey)) {
      setAltResults(cacheRef.current.get(triggerKey));
      return;
    }

    if (!canFetch) return;

    // If a request is already in-flight for this key, reuse it
    if (inflightMap.has(triggerKey)) {
      const results = await inflightMap.get(triggerKey);
      if (results) setAltResults(results);
      return;
    }

    const promise = (async () => {
      const initResult = await fetchData(null);
      if (!initResult) return null;
      const alternativeOffsets =
        inputs?.sv_family === 'SV-L' ? [1, 2, 3] : [1, 2];
      const alternativeResults = await Promise.all(
        alternativeOffsets.map((add) =>
          fetchData(initResult.nbr_windings + add)
        )
      );
      const results = [initResult, ...alternativeResults].filter(Boolean);
      return results.length > 0 ? results : null;
    })();

    inflightMap.set(triggerKey, promise);
    const results = await promise;
    inflightMap.delete(triggerKey);

    if (results) {
      cacheRef.current.set(triggerKey, results);
      setAltResults(results);
    }
  }, [triggerKey, canFetch, fetchData]);

  useEffect(() => {
    loadResults();
    // No abort of shared requests; they are deduped across instances
  }, [loadResults]);

  useEffect(() => {
    const selected = altResults[nav.addWindingsSelected];
    if (!selected) return;
    if (inputs.nbr_windings === selected.nbr_windings) return;

    handleInputChange(
      'nbr_windings',
      selected.nbr_windings,
      inputs,
      setInputs,
      setOptions
    );
  }, [altResults, nav.addWindingsSelected, inputs, setInputs, setOptions]);

  // Precompute display rows
  const rows = useMemo(
    () =>
      altResults.map((alt) => ({
        ...alt,
        nbr_windings_rounded: Math.round(alt.nbr_windings * 100) / 100,
        slope_angle_center_rounded_table:
          Math.round(alt.slope_angle_center * 10) / 10,
        slope_angle_center_rounded_block:
          Math.round(alt.slope_angle_center * 100) / 100,
      })),
    [altResults]
  );

  const isa = inputs.pdp
    ?.filter((p) => p.selected)
    .sort((a, b) => b.isa - a.isa)
    .sort((a, b) => a.max_incline - b.max_incline);

  const heightDisplayUnit = UnitConversion(
    null,
    inputs.unit_system,
    'mm'
  ).displayedUnit;

  const isaValue = isa?.length > 0 ? isa[0].isa : null;

  const windOption = options.find((opt) => opt.field === 'nbr_windings');
  const maxWindWarning =
    windOption && (result?.nbr_windings ?? 0) > windOption.max
      ? `Maximum number of windings is ${windOption.max}`
      : '';

  return (
    <>
      {maxWindWarning !== '' && (
        <InfoButton infoText={maxWindWarning} type="warning" align="left" />
      )}
      <div
        className={`glass windings-table ${compactLayout ? 'svl-inline' : ''}`}
      >
        <table>
          <thead>
            <tr>
              <th className="alt-head">
                <img
                  src={`${process.env.PUBLIC_URL}/icons/windings.png`}
                  alt="windings-icon"
                  className="price-detail-icon"
                />
                <span className="alt-head-tooltip">Number of windings</span>
              </th>
              <th className="alt-head">
                {isSvlFamily ? (
                  <>
                    <img
                      src={`${process.env.PUBLIC_URL}/icons/height-diff.png`}
                      alt="Elevation"
                      className="price-detail-icon"
                    />
                    <span className="alt-head-tooltip">
                      {`Elevation [${heightDisplayUnit}]`}
                    </span>
                  </>
                ) : (
                  <>
                    <img
                      src={`${process.env.PUBLIC_URL}/icons/angle.png`}
                      alt="angle-icon"
                      className="price-detail-icon"
                    />
                    <span className="alt-head-tooltip">
                      Centerline slope angle
                    </span>
                  </>
                )}
              </th>
              {isSvlFamily && (
                <th className="alt-head">
                  <img
                    src={`${process.env.PUBLIC_URL}/icons/upper-end.png`}
                    alt="Upper end height"
                    className="price-detail-icon"
                  />
                  <span className="alt-head-tooltip">
                    {`Upper end height [${heightDisplayUnit}]`}
                  </span>
                </th>
              )}
              {isaValue && !isSvlFamily && (
                <th className="alt-head">
                  <img
                    src={`${process.env.PUBLIC_URL}/icons/incline.png`}
                    alt="incline-icon"
                    className="price-detail-icon"
                  />
                  <span className="alt-head-tooltip">
                    Product stability angle (ISA)
                  </span>
                </th>
              )}
              <th className="alt-head">Price</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((alt, index) => (
              <tr
                key={index}
                onClick={() => handleAlternativeClick(alt, index)}
                className={`${nav.addWindingsSelected === index && 'active'}`}
              >
                <td>{alt.nbr_windings_rounded}</td>
                <td>
                  {isSvlFamily
                    ? (roundToOneDecimal(
                        UnitConversion(alt.h2, inputs.unit_system, 'mm')
                          .displayedValue
                      ) ?? '-')
                    : `${alt.slope_angle_center_rounded_table}°`}
                </td>
                {isSvlFamily && (
                  <td>
                    {roundToOneDecimal(
                      UnitConversion(alt.h3, inputs.unit_system, 'mm')
                        .displayedValue
                    ) ?? '-'}
                  </td>
                )}
                {isaValue && !isSvlFamily && (
                  <td>
                    {Math.round(
                      alt.slope_angle_center_rounded_table * isaValue
                    )}
                    °
                  </td>
                )}
                <td>
                  <PriceDisplay
                    price={alt.price}
                    index={index}
                    region={inputs.region}
                  />
                </td>
                <td>
                  <input
                    type="radio"
                    id={`alt-${index}`}
                    checked={nav.addWindingsSelected === index}
                    onChange={() => handleAlternativeClick(alt, index)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PriceDisplay({ price, index, region }) {
  const currencyMap = {
    1: '€',
    2: '$',
    3: '¥',
    4: '€',
  };
  const currencySymbol = currencyMap[region] ?? '€';

  const roundedPrice = Math.round(price / 100) * 100;

  const formattedPrice = roundedPrice.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const output = isNaN(roundedPrice)
    ? currencySymbol.repeat(index + 1)
    : `${currencySymbol} ${formattedPrice}`;
  return output;
}

export default AlternativeResult;
